import { useEffect, useState } from 'react';

/**
 * Ad Break — v2 port of v1's ad-break.ejs + AdBreakSettings.
 * Displayed during stream ad breaks. Counts down + shows return message.
 *
 * Activation: the widget config sets `endsAt` (epoch ms) and an optional `message`.
 * When `endsAt` is in the future, the widget is shown; otherwise it stays hidden.
 *
 * Config:
 *   endsAt: epoch ms                        (required; widget is hidden when unset / past)
 *   message: string                         (default 'Quick ad break, brb!')
 *   returnMessage: string                   (default 'Welcome back!')
 *   accentColor: string                     (default '#f59e0b')
 *   showReturnFor: seconds                  (default 5 — return message lingers after countdown ends)
 *   bgStyle: 'fullscreen' | 'card'          (default 'card')
 */

function fmt(seconds: number): string {
  const s = Math.max(0, Math.ceil(seconds));
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

export function AdBreakWidget({ fields }: { fields: Record<string, unknown> }) {
  const endsAt = Number(fields.endsAt ?? 0);
  const message = String(fields.message ?? 'Quick ad break, brb!');
  const returnMessage = String(fields.returnMessage ?? 'Welcome back!');
  const accent = String(fields.accentColor ?? '#f59e0b');
  const showReturnFor = Number(fields.showReturnFor ?? 5);
  const bgStyle = (fields.bgStyle as 'fullscreen' | 'card' | undefined) ?? 'card';

  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, []);

  if (!endsAt) return null;

  const remainingMs = endsAt - now;
  const isCountdown = remainingMs > 0;
  const isReturnWindow = remainingMs <= 0 && remainingMs > -showReturnFor * 1000;

  if (!isCountdown && !isReturnWindow) return null;

  const containerStyle: React.CSSProperties =
    bgStyle === 'fullscreen'
      ? {
          position: 'fixed',
          inset: 0,
          display: 'grid',
          placeItems: 'center',
          background: 'rgba(0,0,0,0.85)',
        }
      : {
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        };

  return (
    <div style={{ ...containerStyle, fontFamily: "'Poppins', system-ui, sans-serif" }}>
      <div
        style={{
          background: 'linear-gradient(135deg, rgba(15,12,30,0.95), rgba(22,16,46,0.95))',
          border: `2px solid ${accent}80`,
          borderRadius: 18,
          padding: bgStyle === 'fullscreen' ? '40px 56px' : '20px 32px',
          textAlign: 'center',
          color: 'white',
          boxShadow: `0 12px 48px rgba(0,0,0,0.6), 0 0 64px ${accent}33`,
          minWidth: bgStyle === 'fullscreen' ? 480 : 320,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 28,
            right: 28,
            height: 2,
            background: `linear-gradient(90deg, transparent, ${accent}, transparent)`,
          }}
        />
        <div
          style={{
            fontSize: 11,
            fontWeight: 800,
            letterSpacing: 2,
            color: accent,
            textTransform: 'uppercase',
            marginBottom: 8,
          }}
        >
          {isCountdown ? '⚡ Ad Break' : '✓ Back'}
        </div>
        <div
          style={{
            fontSize: bgStyle === 'fullscreen' ? 28 : 22,
            fontWeight: 700,
            lineHeight: 1.25,
            marginBottom: isCountdown ? 14 : 0,
          }}
        >
          {isCountdown ? message : returnMessage}
        </div>
        {isCountdown && (
          <div
            style={{
              fontSize: bgStyle === 'fullscreen' ? 80 : 56,
              fontWeight: 900,
              fontVariantNumeric: 'tabular-nums',
              color: accent,
              letterSpacing: -1,
              textShadow: `0 0 24px ${accent}aa`,
            }}
          >
            {fmt(remainingMs / 1000)}
          </div>
        )}
      </div>
    </div>
  );
}
